import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DollarSign, Users, Trophy, CalendarClock, Plus, Trash2, Loader2, Star } from 'lucide-react';
import { money } from './BudgetEditor';
import { Modal } from './AdFinancePanel';

/**
 * AD Overview ▸ Morning Briefing widgets (docs/AD_DASHBOARD_AND_FINANCE.md §1):
 *   Financial — bank balance + year-to-date P&L from the Treasurer's OL Finance books
 *               (only when the Treasurer has shared the report viewer), dues A/R and
 *               the latest budget's variance (AD Finance).
 *   Program   — sports, players, families, coaches, leadership, volunteers and the
 *               upcoming dates of the org's monthly task list.
 *   Season    — per sport: % complete, W/L per team, milestones (→ History + Season Highlights).
 * Three compact cards in one row (OL_UI_DESIGN_GUIDE §6a).
 */
const API_URL = process.env.REACT_APP_BACKEND_URL;
const card = 'rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 p-3 flex flex-col min-h-0';
const head = 'flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-white mb-2';
const inputCls = 'rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 px-2 py-1.5 text-sm';

export default function AdOverviewWidgets({ orgId, financeOrgId, reportsGranted, onNavigate }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3" data-testid="ad-overview-widgets">
      <FinancialWidget orgId={orgId} financeOrgId={financeOrgId || orgId} reportsGranted={reportsGranted} onNavigate={onNavigate} />
      <ProgramWidget orgId={orgId} />
      <SeasonWidget orgId={orgId} />
    </div>
  );
}

function Stat({ label, value, cls = '' }) {
  return (
    <div className="rounded-lg bg-slate-50 dark:bg-slate-900/50 px-2 py-1.5">
      <div className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
      <div className={`text-sm font-semibold tabular-nums text-slate-900 dark:text-white ${cls}`}>{value}</div>
    </div>
  );
}

// ── Financial ────────────────────────────────────────────────────────────────
function FinancialWidget({ orgId, financeOrgId, reportsGranted, onNavigate }) {
  const [books, setBooks] = useState(null);     // {bank, net, fyStart} | {none:true}
  const [dues, setDues] = useState(null);
  const [budget, setBudget] = useState(null);
  useEffect(() => {
    let dead = false;
    axios.get(`${API_URL}/api/ad-finance/${orgId}/dues`).then((r) => !dead && setDues(r.data.summary)).catch(() => !dead && setDues({}));
    axios.get(`${API_URL}/api/ad-finance/${orgId}/budgets`).then((r) => !dead && setBudget((r.data.budgets || []).find((b) => b.status === 'approved') || (r.data.budgets || [])[0] || false)).catch(() => !dead && setBudget(false));
    (async () => {
      if (!reportsGranted) { setBooks({ none: 'The Treasurer has not shared the finance reports.' }); return; }
      try {
        const { data: co } = await axios.get(`${API_URL}/api/olfin/linked-company`, { params: { org_id: financeOrgId } });
        if (!co?.company_id) { if (!dead) setBooks({ none: 'No finance company is linked yet.' }); return; }
        const today = new Date().toISOString().slice(0, 10);
        const { data: bs } = await axios.get(`${API_URL}/api/olfin/companies/${co.company_id}/reports/balance-sheet`, { params: { as_of: today } });
        const bank = (bs.rows || []).filter((r) => r.type === 'Bank').reduce((a, r) => a + (Number(r.amount) || 0), 0) / 100;
        const start = bs.fy_start || `${today.slice(0, 4)}-01-01`;
        const { data: pl } = await axios.get(`${API_URL}/api/olfin/companies/${co.company_id}/reports/profit-loss`, { params: { start, end: today } });
        if (!dead) setBooks({ bank, net: (Number(pl.net_income) || 0) / 100, fyStart: start });
      } catch (e) { if (!dead) setBooks({ none: e?.response?.data?.detail || 'Finance reports are not available.' }); }
    })();
    return () => { dead = true; };
  }, [orgId, financeOrgId, reportsGranted]);

  return (
    <div className={card} data-testid="widget-financial">
      <div className={head}><DollarSign className="h-4 w-4 text-emerald-600" /> Financial Overview
        <button type="button" onClick={() => onNavigate?.('ad-finance')} className="ml-auto text-xs font-medium text-blue-600 hover:underline">Open Finance</button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {books === null ? <Stat label="Bank balance" value="…" /> : books.none
          ? <div className="col-span-2 text-[11px] text-slate-500 dark:text-slate-400">{books.none}</div>
          : (<>
            <Stat label="Bank balance" value={money(books.bank)} />
            <Stat label={`Net income since ${books.fyStart}`} value={money(books.net)} cls={books.net < 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'} />
          </>)}
        <Stat label="Dues collected" value={dues ? money(dues.collected) : '…'} cls="text-emerald-600 dark:text-emerald-400" />
        <Stat label="Dues outstanding" value={dues ? money(dues.outstanding) : '…'} cls="text-amber-600 dark:text-amber-400" />
        <Stat label="Overdue" value={dues ? `${money(dues.overdue)} · ${dues.overdue_families ?? 0} fam.` : '…'} cls="text-red-600 dark:text-red-400" />
        {budget === null ? <Stat label="Budget variance" value="…" /> : budget === false
          ? <Stat label="Budget variance" value="no budget yet" />
          : <Stat label={`Budget variance · ${budget.name}${budget.totals.alerts ? ` · ${budget.totals.alerts} line${budget.totals.alerts > 1 ? 's' : ''} flagged` : ''}`} value={money(budget.totals.variance)}
              cls={budget.totals.variance < 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'} />}
      </div>
    </div>
  );
}

// ── Program ──────────────────────────────────────────────────────────────────
function ProgramWidget({ orgId }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    axios.get(`${API_URL}/api/ad-overview/${orgId}/program`).then((r) => setD(r.data)).catch(() => setD({ error: true }));
  }, [orgId]);
  return (
    <div className={card} data-testid="widget-program">
      <div className={head}><Users className="h-4 w-4 text-blue-600" /> Program Overview</div>
      {!d ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : d.error ? <div className="text-xs text-slate-500">Not available.</div> : (<>
        <div className="grid grid-cols-3 gap-2">
          <Stat label="Sports" value={d.sports_count} />
          <Stat label="Players" value={d.players} />
          <Stat label="Families" value={d.families} />
          <Stat label="Coaches" value={d.coaches} />
          <Stat label="Leadership" value={d.leadership} />
          <Stat label="Volunteers" value={d.volunteers} />
        </div>
        <div className="mt-1.5 text-[11px] text-slate-500 dark:text-slate-400" data-testid="program-per-sport">
          {d.sports.map((s) => `${s.label} ${s.players} (${s.teams} teams${s.season_year ? `, ${s.season_year}` : ''})`).join(' · ')}
        </div>
        <div className="mt-2 text-xs font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> Upcoming dates</div>
        <ul className="mt-1 space-y-0.5 max-h-28 overflow-auto text-xs text-slate-700 dark:text-slate-300" data-testid="program-upcoming">
          {d.upcoming.length === 0 && <li className="text-slate-500">Nothing in the next 60 days.</li>}
          {d.upcoming.map((u, i) => (
            <li key={i} className="flex gap-2"><span className="tabular-nums text-slate-500 w-12 shrink-0">{u.date.slice(5)}</span>
              <span className="truncate" title={`${u.title}${u.assignee ? ` — ${u.assignee}` : ''}`}>{u.title}</span>
              {u.assignee && <span className="ml-auto shrink-0 text-[10px] text-slate-400">{u.assignee}</span>}</li>
          ))}
        </ul>
      </>)}
    </div>
  );
}

// ── Season ───────────────────────────────────────────────────────────────────
function SeasonWidget({ orgId }) {
  const [d, setD] = useState(null);
  const [add, setAdd] = useState(null);         // sport row to add a milestone to
  const [list, setList] = useState(false);
  const load = useCallback(() => {
    axios.get(`${API_URL}/api/ad-overview/${orgId}/season`).then((r) => setD(r.data)).catch(() => setD({ error: true }));
  }, [orgId]);
  useEffect(() => { load(); }, [load]);
  const sports = (d?.sports || []).filter((s) => s.season);
  return (
    <div className={card} data-testid="widget-season">
      <div className={head}><Trophy className="h-4 w-4 text-amber-500" /> Season Overview
        <button type="button" onClick={() => setList(true)} className="ml-auto text-xs font-medium text-blue-600 hover:underline" data-testid="season-highlights-open">Season Highlights</button>
      </div>
      {!d ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : d.error ? <div className="text-xs text-slate-500">Not available.</div> : (
        <div className="space-y-2 max-h-64 overflow-auto pr-1">
          {sports.map((s) => (
            <div key={s.code} data-testid={`season-${s.code}`}>
              <div className="flex items-center gap-2 text-xs">
                <span className="font-semibold text-slate-800 dark:text-slate-100">{s.label}</span>
                <span className="text-slate-500">{s.season}</span>
                <span className="ml-auto tabular-nums text-slate-600 dark:text-slate-300">{s.pct}% · {s.played}/{s.games}</span>
                <button type="button" title="Add a milestone" onClick={() => setAdd(s)} className="text-slate-400 hover:text-amber-600" data-testid={`season-add-${s.code}`}><Plus className="h-3.5 w-3.5" /></button>
              </div>
              <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 mt-0.5 overflow-hidden"><div className="h-full bg-amber-500" style={{ width: `${s.pct}%` }} /></div>
              <div className="flex flex-wrap gap-1 mt-1">
                {s.teams.map((t) => (
                  <span key={t.team} className="text-[11px] px-1.5 py-0.5 rounded bg-slate-50 dark:bg-slate-900/50 text-slate-700 dark:text-slate-300">
                    {t.team} <b className="tabular-nums">{t.w}-{t.l}{t.t ? `-${t.t}` : ''}</b>
                  </span>
                ))}
              </div>
              {s.milestones.slice(0, 2).map((m) => (
                <div key={m.id} className="text-[11px] text-slate-600 dark:text-slate-400 flex gap-1 mt-0.5"><Star className="h-3 w-3 text-amber-500 shrink-0 mt-0.5" /><span className="truncate" title={m.detail}>{m.date.slice(5)} {m.title}</span></div>
              ))}
            </div>
          ))}
          {sports.length === 0 && <div className="text-xs text-slate-500">No season games yet.</div>}
        </div>
      )}
      {add && <MilestoneDialog orgId={orgId} sport={add} onClose={() => setAdd(null)} onDone={() => { setAdd(null); load(); }} />}
      {list && <HighlightsDialog orgId={orgId} sports={sports} onClose={() => { setList(false); load(); }} onAdd={(s) => { setList(false); setAdd(s); }} />}
    </div>
  );
}

function MilestoneDialog({ orgId, sport, onClose, onDone }) {
  const [f, setF] = useState({ title: '', detail: '', date: new Date().toISOString().slice(0, 10), season: sport.season, add_to_history: true });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/ad-overview/${orgId}/highlights`, { ...f, sport: sport.code });
      toast.success(f.add_to_history ? 'Milestone added to Season Highlights and History' : 'Milestone added'); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); } finally { setBusy(false); }
  };
  return (
    <Modal title={`Milestone — ${sport.label} ${sport.season}`} onClose={onClose}>
      <div className="space-y-2">
        <label className="text-xs block">Milestone<input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. VG won the NCHEAC State Championship" className={`${inputCls} w-full mt-0.5`} data-testid="milestone-title" /></label>
        <label className="text-xs block">Detail (optional)<textarea rows={2} value={f.detail} onChange={(e) => setF({ ...f, detail: e.target.value })} className={`${inputCls} w-full mt-0.5`} /></label>
        <div className="flex gap-2">
          <label className="text-xs">Date<input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={`${inputCls} block mt-0.5`} /></label>
          <label className="text-xs">Season<input value={f.season} onChange={(e) => setF({ ...f, season: e.target.value })} className={`${inputCls} block mt-0.5 w-28`} /></label>
        </div>
        <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={f.add_to_history} onChange={(e) => setF({ ...f, add_to_history: e.target.checked })} /> Also add to the {sport.label} History timeline</label>
        <div className="flex justify-end"><button type="button" disabled={busy || !f.title.trim()} onClick={save} data-testid="milestone-save"
          className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">Save</button></div>
      </div>
    </Modal>
  );
}

function HighlightsDialog({ orgId, sports, onClose, onAdd }) {
  const seasons = [...new Set(sports.map((s) => s.season))];
  const [season, setSeason] = useState(seasons[0] || '');
  const [rows, setRows] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API_URL}/api/ad-overview/${orgId}/highlights`, { params: season ? { season } : {} })
      .then((r) => setRows(r.data.highlights || [])).catch(() => setRows([]));
  }, [orgId, season]);
  useEffect(() => { load(); }, [load]);
  const label = Object.fromEntries(sports.map((s) => [s.code, s.label]));
  return (
    <Modal title="Season Highlights" onClose={onClose} wide>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select value={season} onChange={(e) => setSeason(e.target.value)} className={inputCls}>
          <option value="">All seasons</option>
          {seasons.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {sports.map((s) => (
          <button key={s.code} type="button" onClick={() => onAdd(s)} className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline"><Plus className="h-3 w-3" /> {s.label}</button>
        ))}
        <button type="button" onClick={() => window.print()} className="ml-auto text-xs font-medium text-slate-600 dark:text-slate-300 hover:underline">Print</button>
      </div>
      <div className="max-h-[60vh] overflow-auto">
        {rows === null ? <Loader2 className="h-4 w-4 animate-spin" /> : rows.length === 0 ? <div className="text-sm text-slate-500">No milestones yet — add one with + on a sport.</div> : (
          <table className="w-full text-sm" data-testid="highlights-table"><tbody>
            {rows.map((h) => (
              <tr key={h.id} className="border-t border-slate-100 dark:border-slate-800 align-top">
                <td className="py-1.5 pr-2 text-xs text-slate-500 whitespace-nowrap">{h.date}</td>
                <td className="pr-2 text-xs whitespace-nowrap">{label[h.sport] || h.sport} · {h.season}</td>
                <td className="pr-2"><div className="font-medium">{h.title}</div>{h.detail && <div className="text-xs text-slate-500">{h.detail}</div>}</td>
                <td className="pr-2 text-[11px] text-slate-400 whitespace-nowrap">{h.in_history ? 'in History' : ''}</td>
                <td className="text-right"><button type="button" title="Remove from Season Highlights (History keeps its line)" className="text-slate-400 hover:text-red-600"
                  onClick={async () => { if (!window.confirm('Remove this milestone from Season Highlights?')) return; await axios.delete(`${API_URL}/api/ad-overview/${orgId}/highlights/${h.id}`); load(); }}>
                  <Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
          </tbody></table>
        )}
      </div>
    </Modal>
  );
}
